import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Avatar, ToggleButton, ToggleButtonGroup, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Menu, MenuItem } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import Drawer from '../../components/Contacts/Details/Drawer'
import BasicDetails from '../../components/Contacts/Details/BasicDetails/BasicDetails';
import NextAction from '../../components/Contacts/Details/NextAction/NextAction';
import FullDetails from '../../components/Contacts/Details/FullDetails/FullDetails';
import Notes from '../../components/Common/Notes/Notes';
import Attachments from '../../components/Contacts/Details/Attachments/Attachments';
import OpenActivities from '../../components/Contacts/Details/OpenActivities/OpenActivities';
import useApiManager from '../../utils/Api/apiManager';
import ClosedActivities from '../../components/Contacts/Details/ClosedActivities/ClosedActivities';
import Deals from '../../components/Contacts/Details/Deals/Deals'
import Campaigns from '../../components/Contacts/Details/Campaigns/Campaigns';
import ProfileAvatar from '../../components/Common/ProfileAvatar/ProfileAvatar';
import EntityTimeline from '../../components/Common/Timeline/Timeline';
import CreateProposalForm from '../../components/Proposals/Page/CreateForm';
import ProposalsDialog from '../../components/Contacts/Details/Proposals/ProposalsDialog';
import { toast } from 'sonner';

const ContactDetails = () => {
    const { leadId } = useParams(); // Get leadID from URL
    const [leadDetails, setLeadDetails] = useState(null);
    const [leadRelatedEntitiesCount, setLeadRelatedEntitiesCount] = useState(null);
    const [latestUpdate, setLatestUpdate] = useState('');
    const [leadNotes, setLeadNotes] = useState([]);
    const [leadAttachments, setLeadAttachments] = useState([]);
    const [leadTasks, setLeadTasks] = useState([]);
    const [leadClosedTasks, setClosedTasks] = useState([]);
    const [deals, setDeals] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openProposalDialog, setOpenProposalDialog] = useState(false);
    const [openProposalsListDialog, setOpenProposalsListDialog] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { get, put, post } = useApiManager();
    const notesRef = useRef(null);
    const attachmentsRef = useRef(null);
    const dealsRef = useRef(null);
    const openActivitiesRef = useRef(null);
    const closedActivitiesRef = useRef(null);
    const campaignsRef = useRef(null);
    const [selectedView, setSelectedView] = useState("overview");

    const handleToggle = (event, newView) => {
        if (newView) {
            setSelectedView(newView);
        }
    };

    const scrollToSection = (ref) => {
        ref.current.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchLeadDetails = async () => {
        setLoading(true);

        try {
            const data = await get(`/leads/${leadId}`);
            setLeadDetails(data);
            const firstName = data.firstName ?? '';
            const lastName = data.lastName ?? '';
            document.title = `${firstName} ${lastName} (Contact) - iLead CRM`.trim() || 'Contact Details';

        } catch (error) {
            console.error('Error fetching lead details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadRelatedEntitiesCount = async () => {
        setLoading(true);

        try {
            const data = await get(`/leads/${leadId}/related-entities-count`);
            setLeadRelatedEntitiesCount(data);
        } catch (error) {
            console.error('Error fetching lead entities count:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLatestUpdate = async () => {
        setLoading(true);

        try {
            const data = await get(`/leads/${leadId}/latest-update`);
            setLatestUpdate(data);
        } catch (error) {
            console.error('Error fetching latest update:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadNotes = async () => {
        setLoading(true);

        try {
            const notes = await get(`/leads/${leadId}/notes`);
            setLeadNotes(notes);
        } catch (error) {
            console.error('Error fetching lead notes:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadAttachments = async () => {
        setLoading(true);

        try {
            const attachments = await get(`/leads/${leadId}/attachments`);
            setLeadAttachments(attachments);
        } catch (error) {
            console.error('Error fetching lead notes:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadTasks = async () => {
        setLoading(true);
        try {
            const tasks = await get(`/leads/${leadId}/tasks/opened`);
            setLeadTasks(tasks);
        } catch (error) {
            console.error('Error fetching lead notes:', error);
        } finally {
            setLoading(false);
        }
    };
    const fetchLeadClosedTasks = async () => {
        setLoading(true);
        try {
            const tasks = await get(`/leads/${leadId}/tasks/closed`);
            setClosedTasks(tasks);
        } catch (error) {
            console.error('Error fetching lead notes:', error);
        } finally {
            setLoading(false);
        }
    };
    const fetchDeals = async () => {
        setLoading(true);
        try {
            const deals = await get(`/contacts/${leadId}/deals`);
            setDeals(deals);
        } catch (error) {
            console.error('Error fetching lead notes:', error);
        } finally {
            setLoading(false);
        }
    };
    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const campaigns = await get(`/contacts/${leadId}/campaigns`);
            setCampaigns(campaigns);
        } catch (error) {
            console.error('Error fetching lead campaigns:', error);
        } finally {
            setLoading(false);
        }
    };

    const refreshLeadNotes = async () => {
        await fetchLeadNotes();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshLeadAttachments = async () => {
        await fetchLeadAttachments();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshLeadTasks = async () => {
        await fetchLeadTasks();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshLeadClosedTasks = async () => {
        await fetchLeadClosedTasks();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshDeals = async () => {
        await fetchDeals();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const refreshCampaigns = async () => {
        await fetchCampaigns();
        await fetchLeadRelatedEntitiesCount();
        await fetchLatestUpdate();
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenProposalDialog = () => {
        setOpenProposalDialog(true);
        handleMenuClose();
    };

    const handleCloseProposalDialog = () => {
        setOpenProposalDialog(false);
    };

    const handleOpenProposalsListDialog = () => {
        setOpenProposalsListDialog(true);
        handleMenuClose();
    };

    const handleCloseProposalsListDialog = () => {
        setOpenProposalsListDialog(false);
    };

    const handleProposalFormSubmit = async (formData) => {
        try {
            const responseData = await post('/proposals', formData);
            if (responseData?.id) {
                handleCloseProposalDialog();
                toast.success("Proposal created successfully!");
            }
        } catch (error) {
            console.error('Failed to submit proposal form:', error);
        }
    };


    useEffect(() => {
        fetchLeadDetails();
        fetchLeadRelatedEntitiesCount();
        fetchLatestUpdate();
        fetchLeadNotes();
        fetchLeadAttachments();
        fetchLeadTasks();
        fetchLeadClosedTasks();
        fetchDeals();
        fetchCampaigns();
    }, []);


    return (
        <div style={{}}>
            <Paper elevation={0} sx={{
                p: 2
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
                    {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {loading ? <CircularProgress /> : <Avatar>{leadDetails?.firstName[0]}</Avatar>}
                        <h3> {loading ? "Loading..." : (leadDetails?.firstName || '') + ' ' + (leadDetails?.lastName || '')}  </h3>
                    </Box> */}
                    <ProfileAvatar pictureUrl={leadDetails?.logoUrl} leadDetails={leadDetails} loading={loading} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                            variant="contained" 
                            onClick={handleMenuClick}
                            aria-controls={Boolean(anchorEl) ? 'proposal-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
                        >
                            Proposals
                        </Button>
                        <Menu
                            id="proposal-menu"
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                        >
                            <MenuItem onClick={handleOpenProposalDialog}>Create Proposal</MenuItem>
                            <MenuItem onClick={handleOpenProposalsListDialog}>View Proposals</MenuItem>
                        </Menu>
                        <Link to={`/contacts/edit/${leadId}`}>
                            <Button variant="outlined">Edit</Button>
                        </Link>
                    </Box>
                </Box>
            </Paper >
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box id='drawer' sx={{ display: 'flex' }}>
                    <Drawer
                        scrollToSection={scrollToSection}
                        sections={{
                            notesRef,
                            attachmentsRef,
                            dealsRef,
                            openActivitiesRef,
                            closedActivitiesRef,
                            campaignsRef
                        }}
                        relatedEntitiesCount={leadRelatedEntitiesCount} />
                </Box>
                <Box id='main' sx={{ flexGrow: 1, paddingTop: 3, paddingRight: 5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 3
                        }}
                    >
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            aria-label="Platform"
                            value={selectedView}
                            onChange={handleToggle}
                            sx={{ bgcolor: 'white' }}
                        >
                            <ToggleButton value="overview" sx={{ color: 'black' }}>
                                Overview
                            </ToggleButton>
                            <ToggleButton value="history" sx={{ color: 'black' }}>
                                History
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Typography variant="body2" sx={{ color: 'gray', marginLeft: 2 }}>
                            {latestUpdate}
                        </Typography>
                    </Box>
                    <Box sx={{ '& > * > *': { marginBottom: 3 } }}>
                        {selectedView === "overview" && (
                            <div id='overview'>
                                <div>
                                    <BasicDetails data={leadDetails} />
                                </div>
                                {/* <div>
                                <NextAction />
                            </div> */}
                                <div>
                                    <FullDetails leadData={leadDetails} />
                                </div>
                                <div ref={notesRef}>
                                    <Notes data={leadNotes} fetchNotes={refreshLeadNotes} entity={'leads'} />
                                </div>
                                <div ref={attachmentsRef}>
                                    <Attachments data={leadAttachments} fetchLeadAttachments={refreshLeadAttachments} />
                                </div>
                                <div ref={dealsRef}>
                                    <Deals data={deals} contactId={leadId} fetchDeals={refreshDeals} />
                                </div>
                                <div ref={openActivitiesRef}>
                                    <OpenActivities
                                        data={leadTasks}
                                        fetchLeadTasks={refreshLeadTasks}
                                        fetchClosedTasks={refreshLeadClosedTasks}
                                    />
                                </div>
                                <div ref={closedActivitiesRef}>
                                    <ClosedActivities data={leadClosedTasks} fetchClosedTasks={refreshLeadClosedTasks} />
                                </div>
                                <div ref={campaignsRef}>
                                    <Campaigns data={campaigns} fetchCampaigns={refreshCampaigns} contactId={leadId} />
                                </div>


                            </div>
                        )}
                        {selectedView === "history" && (
                            <div id='history'>
                                <EntityTimeline entityId={leadId} entityType={'leads'} />
                            </div>
                        )}
                    </Box>
                </Box>
            </Box>

            <Dialog
                fullWidth
                maxWidth={'md'}
                open={openProposalDialog}
                onClose={handleCloseProposalDialog}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
                    <DialogTitle>Create Proposal</DialogTitle>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={handleCloseProposalDialog}>Cancel</Button>
                        <Button variant="contained" type="submit" form="create-form">Save</Button>
                    </Box>
                </Box>
                <Divider aria-hidden="true" sx={{ borderWidth: "thin" }} />
                <DialogContent>
                    <CreateProposalForm onSubmit={handleProposalFormSubmit} leadIdPar={leadId} />
                </DialogContent>
            </Dialog>

            <ProposalsDialog 
                open={openProposalsListDialog}
                onClose={handleCloseProposalsListDialog}
                contactId={leadId}
            />

        </div>
    )
};

export default ContactDetails;